/**
 * Utility functions for Simple Scheduler API calls
 */

/**
 * Fetches data from a given endpoint with proper authentication
 * @param endpoint - API endpoint URL
 * @param method - HTTP method (GET, POST, etc.)
 * @param data - Request body data (for POST, PUT, etc.)
 * @param apiKey - API key for authentication
 * @returns Promise with the response data
 */
export const fetchWithAuth = async (
  endpoint: string,
  method: string = 'GET',
  data?: any,
  apiKey?: string
): Promise<any> => {
  try {
    // Prepare headers with auth if available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    // Prepare request options
    const options: RequestInit = {
      method,
      headers,
    };

    // Add body for non-GET requests
    if (method !== 'GET' && data) {
      options.body = JSON.stringify(data);
    }

    // Make the request
    const response = await fetch(endpoint, options);

    // Log detailed error information for debugging
    if (!response.ok) {
      console.error(
        `API Error (${endpoint}): ${response.status} ${response.statusText}`
      );
      const errorText = await response.text();
      console.error(`Error details: ${errorText}`);
      throw new Error(`API request failed: ${response.status}`);
    }

    // Parse and return response
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`Error in fetchWithAuth for ${endpoint}:`, error);
    throw error;
  }
};

/**
 * Fetches jobs and business units for a team
 * @param teamId - Team ID to fetch data for
 * @param apiKey - API key for authentication
 * @returns Object containing bizUnits and jobs
 */
export const fetchJobsAndBusinessUnits = async (
  teamId: number,
  apiKey?: string
) => {
  try {
    const data = await fetchWithAuth(
      '/api/simple-scheduler/get-st-ids',
      'POST',
      { teamId },
      apiKey
    );

    const stIds = data.stIds || [];

    // If no ST IDs found, return empty objects
    if (!stIds || stIds.length === 0) {
      console.log(`No ST IDs found for team ${teamId}. Using empty values.`);
      return { bizUnits: {}, jobs: {} };
    }

    // Process business units
    const bizUnits = stIds
      .filter((item: any) => item.type === 'BusinessUnit')
      .reduce(
        (
          acc: { [key: string]: number },
          { name, st_id }: { name: string | null; st_id: number | null }
        ) => {
          if (name !== null && st_id !== null) {
            acc[name] = st_id;
          }
          return acc;
        },
        {} as { [key: string]: number }
      );

    // Process jobs
    const jobs = stIds
      .filter((item: any) => item.type === 'Job')
      .reduce(
        (
          acc: { [key: string]: number },
          { name, st_id }: { name: string | null; st_id: number | null }
        ) => {
          if (name !== null && st_id !== null) {
            acc[name] = st_id;
          }
          return acc;
        },
        {} as { [key: string]: number }
      );

    return { bizUnits, jobs };
  } catch (error) {
    console.error('Error fetching jobs and business units:', error);
    return { bizUnits: {}, jobs: {} };
  }
};

/**
 * Searches for a customer by phone number
 * @param phoneNumber - Phone number to search for
 * @param teamId - Team ID to search within
 * @param apiKey - API key for authentication
 * @returns Customer information if found
 */
export const searchCustomerByPhone = async (
  phoneNumber: string,
  teamId: number,
  apiKey?: string
) => {
  try {
    const result = await fetchWithAuth(
      '/api/responder/searchCustomer',
      'POST',
      {
        teamId,
        phone: phoneNumber.replace(/\D/g, ''), // Remove non-numeric characters
      },
      apiKey
    );

    return result;
  } catch (error) {
    console.error('Error searching for customer by phone:', error);
    return null;
  }
};

/**
 * Searches for a customer by address
 * @param address - Address components
 * @param teamId - Team ID to search within
 * @param apiKey - API key for authentication
 * @returns Customer information if found
 */
export const searchCustomerByAddress = async (
  address: {
    street: string;
    unit?: string;
    city: string;
    state: string;
    zip: string;
  },
  teamId: number,
  apiKey?: string
) => {
  try {
    const result = await fetchWithAuth(
      '/api/responder/searchCustomer',
      'POST',
      {
        teamId,
        phone: '', // Empty string for phone
        ...address,
      },
      apiKey
    );

    return result;
  } catch (error) {
    console.error('Error searching for customer by address:', error);
    return null;
  }
};

/**
 * Fetches availability slots by job type
 * @param jobDescription - Job type description
 * @param teamId - Team ID
 * @param apiKey - API key for authentication
 * @returns Available time slots
 */
export const fetchAvailabilities = async (
  jobDescription: string,
  teamId: number,
  apiKey?: string
) => {
  try {
    const result = await fetchWithAuth(
      '/api/responder/vapi/availabilitiesByJT',
      'POST',
      {
        message: {
          toolCalls: [
            {
              id: 'availability-call',
              function: {
                arguments: {
                  jobDescription,
                  team_id: teamId,
                  numberOfAvailableAppointments: 500,
                },
              },
            },
          ],
        },
      },
      apiKey
    );

    return result;
  } catch (error) {
    console.error('Error fetching availabilities:', error);
    return { availabilities: [] };
  }
};

/**
 * Schedules an appointment
 * @param scheduleData - Appointment data
 * @param apiKey - API key for authentication
 * @returns Schedule result
 */
export const scheduleAppointment = async (
  scheduleData: any,
  apiKey?: string
) => {
  try {
    const result = await fetchWithAuth(
      '/api/simple-scheduler/schedule',
      'POST',
      scheduleData,
      apiKey
    );

    return result;
  } catch (error) {
    console.error('Error scheduling appointment:', error);
    throw error; // Re-throw to allow component to handle the error
  }
};

/**
 * Fetches address suggestions based on a query string
 * @param query - Address query string
 * @param apiKey - API key for authentication
 * @returns Address suggestions
 */
export const getAddressSuggestions = async (query: string, apiKey?: string) => {
  try {
    const result = await fetchWithAuth(
      `/api/simple-scheduler/address-suggestions?query=${encodeURIComponent(query)}`,
      'GET',
      undefined,
      apiKey
    );

    return result.addresses || [];
  } catch (error) {
    console.error('Error fetching address suggestions:', error);
    return [];
  }
};

/**
 * Checks if a zip code is serviceable
 * @param zipCode - Zip code to check
 * @param teamId - Team ID
 * @param apiKey - API key for authentication
 * @returns Whether the zip code is serviceable
 */
export const checkZipCodeServiceability = async (
  zipCode: string,
  teamId: number,
  apiKey?: string
) => {
  try {
    const result = await fetchWithAuth(
      '/api/simple-scheduler/check-serviceable-zip',
      'POST',
      {
        zipCode,
        teamId,
      },
      apiKey
    );

    return result.isServiceable || false;
  } catch (error) {
    console.error('Error checking zip code serviceability:', error);
    return false;
  }
};

/**
 * Analyzes an issue description and fetches available time slots in one API call
 * @param issueDescription - Description of the issue
 * @param bizUnitOptions - Available business unit options (object with name:id pairs)
 * @param jobTypeOptions - Available job type options (object with name:id pairs)
 * @param teamId - Team ID
 * @param bizUnitMapping - Business unit name to ID mapping
 * @param jobTypeMapping - Job type name to ID mapping
 * @param apiKey - API key for authentication
 * @param address - Address of the issue
 * @param systemAge - Age of the system (under10 or over10)
 * @param issueSelection - Initial issue selection from stage 1
 * @param additionalAnswers - Additional answers from the issue analysis
 * @param additionalQuestions - Additional questions from the issue analysis
 * @param leadTimeMinutes - Lead time in minutes
 * @param timeframeHours - Custom timeframe hour configuration
 * @param customerMemberships - Customer's membership information
 * @returns Object containing jobType, businessUnit, and availabilities
 */
export const analyzeIssueAndGetAvailabilities = async (
  issueDescription: string,
  bizUnitOptions: Array<{ name: string; id: number }>,
  jobTypeOptions: Array<{ name: string; id: number }>,
  teamId: number,
  bizUnitMapping: { [key: string]: number },
  jobTypeMapping: { [key: string]: number },
  apiKey?: string,
  address?: string,
  systemAge?: string,
  issueSelection?: string,
  additionalAnswers?: Record<string, string>,
  additionalQuestions?: Array<{
    id: string;
    question: string;
    options: Array<{ id: string; label: string }>;
  }>,
  leadTimeMinutes?: number,
  timeframeHours?: {
    morning: { start: number; end: number };
    afternoon: { start: number; end: number };
    allday: { start: number; end: number };
  },
  customerMemberships?: string[]
) => {
  try {
    const result = await fetchWithAuth(
      '/api/simple-scheduler/analyze-and-get-availabilities',
      'POST',
      {
        issueDescription,
        bizUnitOptions,
        jobTypeOptions,
        teamId,
        bizUnitMapping,
        jobTypeMapping,
        address,
        systemAge,
        issueSelection,
        additionalAnswers,
        additionalQuestions,
        leadTimeMinutes,
        timeframeHours,
        customerMemberships,
      },
      apiKey
    );

    return {
      jobType: result.jobType || bizUnitOptions[0]?.name || '',
      businessUnit: result.businessUnit || jobTypeOptions[0]?.name || '',
      availabilities: result.availabilities || [],
      jobTypeId: result.jobTypeId,
      businessUnitId: result.businessUnitId,
    };
  } catch (error) {
    console.error('Error analyzing issue and getting availabilities:', error);
    // Return default values if the request fails
    return {
      jobType: jobTypeOptions[0]?.name || '',
      businessUnit: bizUnitOptions[0]?.name || '',
      availabilities: [],
      jobTypeId: null,
      businessUnitId: null,
    };
  }
};

/**
 * Fetches team configuration from the simple_scheduler_config table
 * @param teamId - Team ID to fetch configuration for
 * @param apiKey - API key for authentication
 * @returns Team configuration with issue types
 */
export const getTeamConfig = async (teamId: string, apiKey?: string) => {
  try {
    const result = await fetchWithAuth(
      `/api/simple-scheduler/get-team-config?teamId=${encodeURIComponent(teamId)}`,
      'GET',
      undefined,
      apiKey
    );

    return result;
  } catch (error) {
    console.error('Error fetching team configuration:', error);
    // Return default configuration on error
    return {
      issue_types: ['HVAC', 'Plumbing', 'Electrical'],
    };
  }
};
